import type { Plan, ResultSet, ScenarioComparison } from '@projections/engine';
import { create } from 'zustand';
import { DEFAULT_RUN } from '../data/config';
import { createPlanStore, type PlanStore } from '../data/planStore';
import { syntheticReturnData } from '../data/returnData';
import { sampleThaiPlan } from '../data/samplePlan';
import {
  runSimulation,
  runStressComparison,
  type SimulationParams,
} from '../data/simulationClient';

/**
 * App-wide UI state (zustand). Holds the ACTIVE plan being edited, the most recent simulation
 * result + stress comparison (kept in memory — full ResultSets with every path's Ledger, for the
 * dashboard and the ledger drill-down), the selected persistence store, and the async status of the
 * Run / Save actions the top bar drives. Screen-local form state lives in react-hook-form; this
 * store is the cross-screen handoff (editor → dashboard → ledger).
 *
 * The Run/Save THUNKS (`runActive` / `saveActive`) are the app-shell seam: they call the
 * `simulationClient` / `returnData` / `planStore` data layer. Those data bodies are filled by the
 * data-layer build agent; until then a thunk surfaces the thrown stub as an error status instead of
 * crashing the shell, so the app shell itself stays green and wires up cleanly the moment the data
 * bodies land.
 */

export type RunStatus = 'idle' | 'running' | 'done' | 'error';
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface AppState {
  /** The persistence backend (RPC or localStorage), chosen once at startup. */
  readonly store: PlanStore;
  /** The plan currently open in the editor. */
  plan: Plan;
  /** The id of the persisted plan (null = unsaved/new). */
  planId: string | null;
  /** The latest in-memory simulation result (null until a run). */
  result: ResultSet | null;
  /** The latest stress comparison (null until run). */
  stress: ScenarioComparison | null;
  /** Params of the last run, echoed so the ledger drill-down can re-derive a path. */
  lastRunSeed: number;
  /** The parameters the simulate form / top-bar Run uses (mirrors the API SimulateRequest). */
  runParams: SimulationParams;

  /** Async status of a client-side Run. */
  runStatus: RunStatus;
  /** Error message from the last failed Run (null otherwise). */
  runError: string | null;
  /** Async status of a Save to the plan store. */
  saveStatus: SaveStatus;
  /** Error message from the last failed Save (null otherwise). */
  saveError: string | null;

  setPlan: (plan: Plan) => void;
  setPlanId: (id: string | null) => void;
  setResult: (result: ResultSet | null) => void;
  setStress: (stress: ScenarioComparison | null) => void;
  setLastRunSeed: (seed: number) => void;
  setRunParams: (params: Partial<SimulationParams>) => void;

  /**
   * Run the Monte Carlo CLIENT-SIDE: assemble a `ReturnData` (synthetic illustrative data offline),
   * run the simulation + the stress comparison, and store both. Returns true on success. The
   * `onResult` callback (e.g. `() => navigate('results')`) runs only on success. Errors (incl. a
   * still-stubbed data body) land in `runStatus:'error'` + `runError`.
   */
  runActive: (onResult?: () => void) => Promise<boolean>;

  /** Persist the active plan via the plan store (API or localStorage). Returns true on success. */
  saveActive: () => Promise<boolean>;

  /**
   * One-shot startup hydration: load the most-recently-updated saved plan (if any) from the active
   * plan store into the editor, so a returning user sees their own plan rather than the sample.
   * Best-effort — any failure (empty store, offline API) silently leaves the sample plan in place.
   * Idempotent: it never clobbers a plan the user has already saved this session (`planId !== null`).
   */
  hydrate: () => Promise<void>;
}

const DEFAULT_RUN_PARAMS: SimulationParams = {
  sampler: DEFAULT_RUN.sampler,
  nPaths: DEFAULT_RUN.nPaths,
  seed: DEFAULT_RUN.seed,
  taxPolicy: DEFAULT_RUN.taxPolicy,
};

/** Best-effort message extraction from an unknown thrown value. */
function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export const useAppStore = create<AppState>((set, get) => ({
  store: createPlanStore(),
  plan: sampleThaiPlan(),
  planId: null,
  result: null,
  stress: null,
  lastRunSeed: DEFAULT_RUN.seed,
  runParams: DEFAULT_RUN_PARAMS,

  runStatus: 'idle',
  runError: null,
  saveStatus: 'idle',
  saveError: null,

  setPlan: (plan) => set({ plan }),
  setPlanId: (planId) => set({ planId }),
  setResult: (result) => set({ result }),
  setStress: (stress) => set({ stress }),
  setLastRunSeed: (lastRunSeed) => set({ lastRunSeed }),
  setRunParams: (params) => set((s) => ({ runParams: { ...s.runParams, ...params } })),

  runActive: async (onResult) => {
    const { plan, runParams } = get();
    set({ runStatus: 'running', runError: null });
    try {
      // Offline default: a small synthetic, illustrative cross-section over the plan's allocation
      // keys (the data agent fills `syntheticReturnData`/`returnDataFromSeries`). When an API/series
      // source is wired this is where real EdgeSeries would be assembled instead.
      const data = syntheticReturnData();
      const result = runSimulation(plan, data, runParams);
      // Stress comparison is best-effort: a failure here must not sink an otherwise-good run.
      let stress: ScenarioComparison | null = null;
      try {
        stress = runStressComparison(plan, data, runParams);
      } catch {
        stress = null;
      }
      set({
        result,
        stress,
        lastRunSeed: runParams.seed,
        runStatus: 'done',
        runError: null,
      });
      onResult?.();
      return true;
    } catch (err) {
      set({ runStatus: 'error', runError: errorMessage(err) });
      return false;
    }
  },

  saveActive: async () => {
    const { store, plan, planId } = get();
    const personName = plan.household.people[0]?.name ?? 'Untitled plan';
    set({ saveStatus: 'saving', saveError: null });
    try {
      if (planId === null) {
        const record = await store.create({ name: personName, document: plan });
        set({ planId: record.id, saveStatus: 'saved', saveError: null });
      } else {
        const record = await store.update(planId, { name: personName, document: plan });
        // The persisted plan vanished underneath us (deleted elsewhere) → recreate it.
        if (record === null) {
          const created = await store.create({ name: personName, document: plan });
          set({ planId: created.id, saveStatus: 'saved', saveError: null });
        } else {
          set({ saveStatus: 'saved', saveError: null });
        }
      }
      return true;
    } catch (err) {
      set({ saveStatus: 'error', saveError: errorMessage(err) });
      return false;
    }
  },

  hydrate: async () => {
    const { store, planId } = get();
    // Don't overwrite a plan the user is already working on this session.
    if (planId !== null) return;
    try {
      const summaries = await store.list();
      const newest = summaries[0]; // store.list() returns newest-updated first
      if (newest === undefined) return;
      const record = await store.get(newest.id);
      if (record === null) return;
      set({ plan: record.document, planId: record.id });
    } catch {
      // Best-effort: leave the sample plan in place on any failure (offline API, parse error, …).
    }
  },
}));
